/**
 * @fileoverview Calendar utilities for mapping tasks to FullCalendar events.
 * Handles timezone conversion, duration computation, and all-day event logic.
 */

/**
 * Default task duration in minutes when not specified.
 */
export const DEFAULT_DURATION_MINUTES = 30;

/**
 * Goal color palette for calendar events - TEXT colors (darker/saturated).
 * Maps color_key (0-5) to match the goalPalette semantic tokens.
 * These are darker versions suitable for text on light backgrounds.
 *
 * Mapping: 0=gray (system), 1=green, 2=yellow, 3=orange, 4=red, 5=purple
 */
/* eslint-disable no-restricted-syntax */
/**
 * Goal color palette for calendar events - TEXT colors (darker/saturated).
 * Maps color_key (0-5) to match the goalPalette semantic tokens.
 * These are darker versions suitable for text on light backgrounds.
 *
 * Mapping: 0=gray (system), 1=green, 2=yellow, 3=orange, 4=red, 5=purple
 */
const GOAL_TEXT_COLORS = [
  '#6B7280', // 0: gray (system goal)
  '#16A34A', // 1: green (darker than goalPalette.1)
  '#CA8A04', // 2: yellow/amber (darker for readability)
  '#EA580C', // 3: orange
  '#DC2626', // 4: red
  '#9333EA', // 5: purple
];

/**
 * Goal color palette for calendar events - BACKGROUND colors (light/pastel).
 * Using the goalPalette semantic token hex values with transparency.
 */
const GOAL_BG_COLORS = [
  'rgba(211, 211, 211, 0.4)', // 0: gray
  'rgba(187, 247, 208, 0.5)', // 1: green (#BBF7D0)
  'rgba(253, 230, 138, 0.5)', // 2: yellow (#FDE68A)
  'rgba(254, 215, 170, 0.5)', // 3: orange (#FED7AA)
  'rgba(254, 202, 202, 0.5)', // 4: red (#FECACA)
  'rgba(233, 213, 255, 0.5)', // 5: purple (#E9D5FF)
];

const FALLBACK_TEXT_COLOR = '#6B7280';
const FALLBACK_BG_COLOR = 'rgba(107, 114, 128, 0.3)';
/* eslint-enable no-restricted-syntax */

/**
 * Get the text color for a goal color_key (for calendar events).
 * @param {number | null | undefined} colorKey
 * @returns {string}
 */
export function getGoalTextColor(colorKey) {
  // Default to 1 (green) for undefined/null, matching constants/colors.js behavior
  const n = typeof colorKey === 'number' && Number.isFinite(colorKey) ? colorKey : 1;
  const idx = Math.min(5, Math.max(0, n));
  return GOAL_TEXT_COLORS[idx] || FALLBACK_TEXT_COLOR;
}

/**
 * Get the light background color for a goal color_key.
 * @param {number | null | undefined} colorKey
 * @returns {string}
 */
export function getGoalBgColor(colorKey) {
  // Default to 1 (green) for undefined/null, matching constants/colors.js behavior
  const n = typeof colorKey === 'number' && Number.isFinite(colorKey) ? colorKey : 1;
  const idx = Math.min(5, Math.max(0, n));
  return GOAL_BG_COLORS[idx] || FALLBACK_BG_COLOR;
}

/**
 * Compute end_time from start_time and duration.
 * If end_time exists but disagrees with computed value, prefer computed.
 * @param {string | null | undefined} startTime - ISO timestamp
 * @param {string | null | undefined} endTime - ISO timestamp (may be stale)
 * @param {number | null | undefined} durationMinutes
 * @returns {Date | null}
 */
export function computeEndTime(startTime, endTime, durationMinutes) {
  if (!startTime) return null;

  const start = new Date(startTime);
  if (isNaN(start.getTime())) return null;

  const duration =
    typeof durationMinutes === 'number' && durationMinutes > 0
      ? durationMinutes
      : DEFAULT_DURATION_MINUTES;

  const computedEnd = new Date(start.getTime() + duration * 60 * 1000);

  // If end_time exists and matches computed, use it; otherwise prefer computed
  if (endTime) {
    const existingEnd = new Date(endTime);
    if (!isNaN(existingEnd.getTime())) {
      const diff = Math.abs(existingEnd.getTime() - computedEnd.getTime());
      // Allow 1 minute tolerance for rounding
      if (diff < 60 * 1000) {
        return existingEnd;
      }
    }
  }

  return computedEnd;
}

/**
 * Check if an event is visible in the calendar.
 * A task/event appears in calendar if it has start_time or start (scheduled).
 * Deadline is independent - tasks without deadlines can still be scheduled.
 * @param {Record<string, any>} event - Task or external event object
 * @returns {boolean}
 */
export function isCalendarVisible(event) {
  if (!event) return false;
  // Event is visible if it has a start time
  // Tasks use start_time, external events use start (after mapping in calendarEnhanced.js)
  // Deadline is NOT required for calendar visibility
  return !!(event.start_time || event.start);
}

/**
 * Map a task to a FullCalendar event object.
 * @param {Record<string, any>} task
 * @param {string} userTimezone - IANA timezone string
 * @returns {import('@fullcalendar/core').EventInput | null}
 */
export function taskToCalendarEvent(task, userTimezone) {
  if (!isCalendarVisible(task)) return null;

  const isCompleted = task.done || task.is_completed || task.completed || false;
  const colorKey = task.linked_goal_color_key ?? task.goal_color_key ?? null;
  const goalColor = getGoalTextColor(colorKey);
  const backgroundColor = getGoalBgColor(colorKey);

  // All-day event handling
  if (task.is_all_day && task.all_day_date) {
    return {
      id: task.id,
      title: task.title || task.task || 'Untitled',
      start: task.all_day_date, // YYYY-MM-DD string for all-day
      allDay: true,
      backgroundColor,
      borderColor: goalColor,
      textColor: goalColor,
      classNames: isCompleted ? ['fc-event-completed'] : [],
      extendedProps: {
        taskId: task.id,
        isCompleted,
        goalId: task.linked_goal_id || task.goal_id,
        goalTitle: task.linked_goal_title,
        colorKey,
        updatedAt: task.updated_at,
      },
    };
  }

  // Timed event
  const startTime = task.start_time;
  const endTime = computeEndTime(startTime, task.end_time, task.estimated_duration_minutes);

  if (!startTime || !endTime) return null;

  return {
    id: task.id,
    title: task.title || task.task || 'Untitled',
    start: startTime,
    end: endTime.toISOString(),
    allDay: false,
    backgroundColor,
    borderColor: goalColor,
    textColor: goalColor,
    classNames: isCompleted ? ['fc-event-completed'] : [],
    extendedProps: {
      taskId: task.id,
      isCompleted,
      goalId: task.linked_goal_id || task.goal_id,
      goalTitle: task.linked_goal_title,
      colorKey,
      durationMinutes: task.estimated_duration_minutes || DEFAULT_DURATION_MINUTES,
      updatedAt: task.updated_at,
    },
  };
}

/**
 * Convert drag/resize result to task update payload.
 * @param {import('@fullcalendar/core').EventApi} event - FullCalendar event
 * @param {string} userTimezone - IANA timezone string
 * @returns {{ start_time: string; end_time: string; estimated_duration_minutes: number; is_all_day: boolean; all_day_date: string | null }}
 */
export function eventToTaskUpdate(event, userTimezone) {
  const start = event.start;
  const end = event.end;
  const allDay = event.allDay;

  if (allDay && start) {
    // All-day event: store local date in all_day_date
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    const allDayDate = `${y}-${m}-${d}`;

    return {
      start_time: start.toISOString(),
      end_time: end ? end.toISOString() : start.toISOString(),
      estimated_duration_minutes: 0,
      is_all_day: true,
      all_day_date: allDayDate,
    };
  }

  // Timed event
  const startIso = start ? start.toISOString() : new Date().toISOString();
  const endDate =
    end || (start ? new Date(start.getTime() + DEFAULT_DURATION_MINUTES * 60 * 1000) : new Date());
  const endIso = endDate.toISOString();

  // Compute duration from start and end
  const durationMs = endDate.getTime() - (start ? start.getTime() : endDate.getTime());
  const durationMinutes = Math.max(1, Math.round(durationMs / (60 * 1000)));

  return {
    start_time: startIso,
    end_time: endIso,
    estimated_duration_minutes: durationMinutes,
    is_all_day: false,
    all_day_date: null,
  };
}

/**
 * Deduplicate events by id and updated_at to prevent duplicates from
 * optimistic updates combined with realtime echoes.
 * @param {Array<import('@fullcalendar/core').EventInput>} events
 * @returns {Array<import('@fullcalendar/core').EventInput>}
 */
export function deduplicateEvents(events) {
  const seen = new Map();

  for (const event of events) {
    const id = event.id;
    if (!id) continue;

    const existing = seen.get(id);
    if (!existing) {
      seen.set(id, event);
      continue;
    }

    // Keep the one with the later updated_at
    const existingUpdatedAt = existing.extendedProps?.updatedAt;
    const newUpdatedAt = event.extendedProps?.updatedAt;

    if (newUpdatedAt && existingUpdatedAt) {
      if (new Date(newUpdatedAt) > new Date(existingUpdatedAt)) {
        seen.set(id, event);
      }
    } else if (newUpdatedAt && !existingUpdatedAt) {
      seen.set(id, event);
    }
  }

  return Array.from(seen.values());
}

/**
 * Check if a task overlaps with a date range (for calendar range queries).
 * Uses the pattern: start_time < rangeEnd AND end_time >= rangeStart
 * @param {Record<string, any>} task
 * @param {Date} rangeStart
 * @param {Date} rangeEnd
 * @returns {boolean}
 */
export function taskOverlapsRange(task, rangeStart, rangeEnd) {
  if (!task.start_time) return false;

  const taskStart = new Date(task.start_time);
  if (isNaN(taskStart.getTime())) return false;

  const taskEnd = computeEndTime(task.start_time, task.end_time, task.estimated_duration_minutes);

  if (!taskEnd) return false;

  // Overlap check: start < rangeEnd AND end >= rangeStart
  return taskStart < rangeEnd && taskEnd >= rangeStart;
}
